import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { combineLatest, EMPTY, from, Observable, of, ReplaySubject } from 'rxjs';
import { environment } from '@env/environment';
import { catchError, filter, finalize, first, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { BillsRepository } from '@data-layer/repositories';
import { BillModel } from '@data-layer/models/payments';
import {
  enumPaymentBillType,
  enumPaymentProvider,
  enumPaymentState,
  enumStripePaymentError,
  enumStripePaymentState,
} from '@data-layer/enums/payments';
import { PaymentService } from '@app/modules/payment/services';
import { EntityModel } from '@data-layer/models/entities';
import { DOCUMENT } from '@angular/common';
import { STRIPE_PAYMENT_FLAG } from '@app/modules/payment/const';
import { AlertService } from '@services/alert.service';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { CompanyModel } from '@data-layer/models/companies';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { enumGACheckoutSteps } from '@enum/enumGACheckoutSteps';
import { OrganizationModel } from '@app/data-layer/models/organizations';
import { ActivatedRoute } from '@angular/router';
import { PaginatedResultModel } from '@app/data-layer/models/common';
import { enumEnvironmentName } from '@env/enumEnvironmentName';

declare let Stripe: any;

@Component({
  selector: 'stripe-payment',
  templateUrl: './stripe-payment.component.html',
  styleUrls: ['./stripe-payment.component.scss'],
})
export class StripePaymentComponent implements AfterViewInit, OnDestroy {
  @Input() public bill: BillModel[];
  @Input() public referenceNumbers: string[] | Observable<string[]>;
  @Input() public organization: OrganizationModel;
  @Input() public company: CompanyModel;
  @Input() public paymentBillType: enumPaymentBillType = enumPaymentBillType.CUSTOM;
  @Input() public paymentCodeId: number;
  @Input() public paymentMethodId: number;
  @Input() public billToEntity: EntityModel;
  @Input() public coupon: string;
  @Input() public returnUrl: string = '/';
  @Input() public isSetupPage: boolean;
  @Input() public subscriptionId: number;
  @Output() public billCreated: EventEmitter<BillModel[]> = new EventEmitter<BillModel[]>();
  @Output() public cancelSetup: EventEmitter<void> = new EventEmitter<void>();
  @Output() public purchase: EventEmitter<{ transactionId: string; bill: BillModel }> = new EventEmitter<{
    transactionId: string;
    bill: BillModel;
  }>();
  @Output() public successPayment: EventEmitter<{
    state: enumPaymentState;
    bill: BillModel;
  }> = new EventEmitter<{
    state: enumPaymentState;
    bill: BillModel;
  }>();

  @ViewChild('paymentForm', { read: ElementRef, static: false })
  public paymentForm: ElementRef;
  @ViewChild('paymentElement', { read: ElementRef, static: false })
  public paymentElement: ElementRef;

  public stripe: any;
  public isShowButton: boolean;
  public isLoading: boolean;

  private stripeScriptId: string = 'stripe';
  private stripeElements: any;
  private stripeAppearance: any;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer2: Renderer2,
    private billsRepository: BillsRepository,
    private paymentService: PaymentService,
    private alertService: AlertService,
    private cdr: ChangeDetectorRef,
    private ga: GoogleAnalyticsService,
    private route: ActivatedRoute,
  ) {}

  public ngAfterViewInit(): void {
    this.isLoading = true;
    this.initStripe();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public handleSubmit(e: ErrorEvent): void {
    e.preventDefault();

    const confirmation$ = this.isSetupPage ? this.confirmSetup() : this.confirmPayment();

    confirmation$
      .pipe(
        takeUntil(this.destroy$),
        switchMap((result: { error?: { type: enumStripePaymentError; message?: string } }) => {
          // If Stripe returns an error, handle it and stop
          if (result?.error) {
            if (result.error.type === enumStripePaymentError.CARD_ERROR) {
              this.alertService.modalError(result.error.message || 'LABELS.SOMETHING_DONT_WORK_TITLE_BANK');
            } else if (result.error.type === enumStripePaymentError.VALIDATION_ERROR) {
              // Validation error - Stripe shows inline errors, do nothing
            } else {
              this.alertService.modalError('LABELS.SOMETHING_DONT_WORK_MESSAGE');
            }
            return EMPTY; // Stop the flow - no redirect, no success emission
          }

          // No error means Stripe will auto-redirect, but we can emit events first
          // Note: In practice, confirmPayment with return_url redirects immediately on success
          // so this code may not execute. But keeping it for safety.
          return this.getBill(this.bill?.[0]?.id);
        }),
        catchError((error) => {
          console.error('Payment confirmation failed', error);
          this.alertService.modalError('LABELS.SOMETHING_DONT_WORK_MESSAGE');
          return EMPTY;
        }),
      )
      .subscribe((result: PaginatedResultModel<BillModel>) => {
        if (!!result) {
          this.purchase.emit({
            transactionId: (result?.items?.[0]?.providerTransactionId || '')?.toString(),
            bill: result?.items?.[0],
          });

          this.onSuccessPayment(result?.items?.[0]?.state, result?.items?.[0]);
        }
      });
  }

  private loadStripe(): Observable<any> {
    const isScriptExist: boolean = !![...(this.document.scripts as any)].find(
      (script: HTMLScriptElement) => script.id === this.stripeScriptId,
    );
    if (isScriptExist) {
      return from(Promise.resolve());
    }
    const scriptPromise: Promise<any> = new Promise((resolve: any) => {
      const scriptSrc: HTMLScriptElement = this.renderer2.createElement('script');
      scriptSrc.id = this.stripeScriptId;
      scriptSrc.src = 'https://js.stripe.com/v3/';
      scriptSrc.onload = resolve;
      this.renderer2.appendChild(this.document.head, scriptSrc);
    });

    return from(scriptPromise);
  }

  private initStripe(): void {
    this.loadStripe()
      .pipe(
        tap(() => {
          this.stripe = Stripe(environment.stripeKey, { locale: 'en' });
        }),
        switchMap(() => {
          if (this.isSetupPage) {
            return of([]);
          } else {
            return this.getPaymentId();
          }
        }),
        switchMap((bill: BillModel[]) => {
          if (this.isSetupPage) {
            return this.setupIntent();
          } else {
            return this.getIntent(bill?.[0]?.id);
          }
        }),
        switchMap((stripeData: { clientSecret: string }) => combineLatest([of(stripeData.clientSecret), this.checkStatus()])),
        finalize(() => (this.isLoading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe(([clientSecret, statusData]: [string, { paymentIntent: { status: enumStripePaymentState } }]) => {
        this.handlePaymentStatus(statusData);

        this.checkOutStep({
          bill: this.bill?.[0],
          checkoutStep: enumGACheckoutSteps.CreatePayment,
          transactionId: (this.bill?.[0]?.providerTransactionId || '').toString(),
        });

        this.stripeAppearance = {
          theme: 'stripe',
          variables: {
            colorPrimary: '#0ac2bb',
            colorBackground: '#ffffff',
            colorText: '#3b3b3b',
            fontFamily: 'Gilroy, Roboto, sans-serif',
          },
        };
        const fontsConfig =
          environment.name === enumEnvironmentName.LOCAL || environment.name === enumEnvironmentName.LOCAL_DEV
            ? []
            : [
                {
                  family: 'Gilroy',
                  src: 'local("Gilroy"), local("gilroy"), url(https://app.eezyimport.com/assets/fonts/3927C6_1_0.woff)',
                },
              ];

        this.stripeElements = this.stripe.elements({
          appearance: this.stripeAppearance,
          fonts: fontsConfig,
          clientSecret,
        });

        const paymentElement: any = this.stripeElements.create('payment', {
          layout: 'tabs',
        });

        // Add event listener for successful load
        paymentElement.on('ready', () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        });

        // Add event listener for load errors
        paymentElement.on('loaderror', (event: { error: { message: string } }) => {
          console.error('Payment Element load error:', event.error);
          this.isLoading = false;
          this.alertService.modalError(event.error?.message || 'LABELS.SOMETHING_DONT_WORK_MESSAGE');
          this.cdr.markForCheck();
        });

        paymentElement.mount(this.paymentElement.nativeElement);

        this.cdr.markForCheck();
      });
  }

  private getPaymentId(): Observable<BillModel[]> {
    if (this.bill) {
      return of(this.bill);
    }

    return this.paymentService
      .handleBill(
        this.paymentBillType,
        enumPaymentProvider.STRIPE,
        this.referenceNumbers,
        this.company.id,
        this.paymentCodeId,
        this.billToEntity,
        this.coupon,
        this.paymentMethodId,
      )
      .pipe(
        map((bill: BillModel[]) => bill.filter((item: BillModel) => item.providerType === enumCompanyPaymentType.INSTANT)),
        tap((bill: BillModel[]) => (this.bill = bill)),
      );
  }

  private getBill(billId: number): Observable<PaginatedResultModel<BillModel>> {
    const params = !!this.referenceNumbers?.[0]
      ? {
          referenceNumber: this.referenceNumbers?.[0],
        }
      : {};

    if (!this.company) {
      return of(null);
    } else {
      return this.billsRepository.search(this.company?.id, { ...params }, true).pipe(
        filter(
          (result: PaginatedResultModel<BillModel>) =>
            (result?.items || []).find((item: BillModel) => item.id === billId)?.state !== enumPaymentState.DELAYED,
        ),
        first(),
      );
    }
  }

  private getIntent(billId: number): Observable<{ clientSecret: string }> {
    return this.billsRepository.createPaymentIntent(billId);
  }

  private setupIntent(): Observable<{ clientSecret: string }> {
    if (!!this.organization?.id) {
      return this.billsRepository.setupOrganizationIntent(this.organization?.id, this.paymentCodeId);
    } else {
      return this.billsRepository.setupIntent(this.company?.id, this.paymentCodeId);
    }
  }

  private checkStatus(): Observable<{ paymentIntent: { status: enumStripePaymentState } }> {
    const clientSecret: string = new URLSearchParams(window.location.search).get('payment_intent_client_secret');

    if (!clientSecret) {
      return of(null);
    }

    return from(this.stripe.retrievePaymentIntent(clientSecret)) as unknown as Observable<{
      paymentIntent: { status: enumStripePaymentState };
    }>;
  }

  private handlePaymentStatus(data: { paymentIntent: { status: enumStripePaymentState } }): void {
    switch (data?.paymentIntent?.status) {
      case enumStripePaymentState.SUCCEEDED:
        this.isShowButton = false;
        break;
      case enumStripePaymentState.PROCESSING:
        this.isShowButton = false;
        break;
      case enumStripePaymentState.REQUIRES_PAYMENT_METHOD:
        this.isShowButton = true;
        break;
      default:
        this.isShowButton = true;
        break;
    }
  }

  private confirmPayment(): Observable<any> {
    return from(
      this.stripe.confirmPayment({
        elements: this.stripeElements,
        confirmParams: {
          return_url: `${this.returnUrl}&billId=${this.bill?.[0]?.id}&${STRIPE_PAYMENT_FLAG}=true`, // later we will check this params in PaymentResultPageComponent
          shipping: {
            name: this.billToEntity?.name,
            phone: this.billToEntity?.phone,
            address: {
              country: this.billToEntity?.address?.country?.description,
              city: this.billToEntity?.address?.city,
              line1: this.billToEntity?.address?.line1,
              line2: `${this.billToEntity?.address?.line2 || ''} ${this.billToEntity?.address?.line3 || ''}`,
              postal_code: this.billToEntity?.address?.postalCode,
              state: this.billToEntity?.address?.state?.code,
            },
          },
        },
      }),
    ) as unknown as Observable<any>;
  }

  private confirmSetup(): Observable<any> {
    const params = this.route.snapshot.queryParams;
    const queryParamSymbol = !!params && Object.keys(params).length ? '&' : '?';
    const subscriptionIdParam = !!this.subscriptionId ? `subscriptionId=${this.subscriptionId}` : '';
    return from(
      this.stripe.confirmSetup({
        elements: this.stripeElements,
        confirmParams: {
          return_url: `${this.returnUrl + queryParamSymbol}${subscriptionIdParam}`,
        },
      }),
    ) as unknown as Observable<any>;
  }

  private checkOutStep(data: { checkoutStep: enumGACheckoutSteps; transactionId: string; bill: BillModel }): void {
    this.ga.checkOutStep(data.bill, data.checkoutStep, data.transactionId);
  }

  private onSuccessPayment(state: enumPaymentState, bill: BillModel) {
    if ([enumEnvironmentName.LOCAL_DEV, enumEnvironmentName.DEV, enumEnvironmentName.TEST].includes(environment.name)) {
      console.log('STRIPE - onSuccessPayment: ', state, bill);
    }

    this.successPayment.emit({ state, bill });
  }
}
